import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, RotateCcw, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { recommendGrades, FINDER_APPLICATIONS, FINDER_COLOURS } from "../lib/gradeFinder";
import { useLang, usePath } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const slug = (s) => s.toLowerCase().replace(/[\s.]+/g, "-");

// Any component can open the wizard by calling this.
export const openGradeFinder = () => window.dispatchEvent(new CustomEvent("clara:open-finder"));

const PREFS = ["Natural", "Alkalized", "None"];

export const GradeFinder = () => {
  const { t } = useLang();
  const lp = usePath();
  const navigate = useNavigate();
  const F = t.finder;
  const P = t.products;
  const G = t.gradePg;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [app, setApp] = useState(null);
  const [colour, setColour] = useState(null);
  const [pref, setPref] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const handler = () => {
      setStep(1); setApp(null); setColour(null); setPref(null); setOpen(true);
      if (products.length === 0) axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    };
    window.addEventListener("clara:open-finder", handler);
    return () => window.removeEventListener("clara:open-finder", handler);
  }, [products.length]);

  const result = app && colour && pref ? recommendGrades(products, app, colour, pref) : null;

  const go = (path) => { setOpen(false); navigate(path); };

  const OptionBtn = ({ testid, selected, onClick, children }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-3 rounded-full border text-sm font-medium transition-colors ${
        selected
          ? "bg-clara-terracotta border-clara-terracotta text-clara-cream"
          : "border-clara-cream/25 text-clara-cream/80 hover:border-clara-cream"
      }`}
    >
      {children}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl p-0 border-0 bg-clara-espresso text-clara-cream overflow-hidden noise-overlay" data-testid="grade-finder-modal">
        <DialogTitle className="sr-only">{F.eyebrow}</DialogTitle>
        <button onClick={() => setOpen(false)} data-testid="gf-close-btn" aria-label="Close" className="absolute top-4 right-4 z-20 text-clara-cream/70 hover:text-clara-cream transition-colors">
          <X className="w-5 h-5" />
        </button>
        <div className="relative z-10 p-8 lg:p-12">
          <p className="eyebrow text-clara-terracotta mb-2">{F.eyebrow}</p>
          {step < 4 && <p className="font-data text-[11px] tracking-[0.2em] uppercase text-clara-darkmuted mb-6" data-testid="gf-step-indicator">{F.step(step)}</p>}

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight mb-8">{F.q1}</h3>
                <div className="flex flex-wrap gap-3">
                  {FINDER_APPLICATIONS.map((a) => (
                    <OptionBtn key={a} testid={`gf-app-${slug(a)}`} selected={app === a} onClick={() => { setApp(a); setStep(2); }}>
                      {P.appLabels[a]}
                    </OptionBtn>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight mb-8">{F.q2}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {FINDER_COLOURS.map((c) => {
                    const sample = products.find((p) => p.color === c);
                    return (
                      <button key={c} onClick={() => { setColour(c); setStep(3); }} data-testid={`gf-colour-${slug(c)}`}
                        className={`group border p-4 text-left transition-colors ${colour === c ? "border-clara-terracotta bg-clara-terracotta/10" : "border-clara-cream/20 hover:border-clara-cream/50"}`}>
                        <span className="block w-full h-10 mb-3 rounded-sm" style={{ backgroundColor: sample?.hex || "#5C3721" }} />
                        <span className="text-sm text-clara-cream/90">{P.colours[c]}</span>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => setStep(1)} data-testid="gf-back-2" className="mt-8 inline-flex items-center gap-2 font-data text-xs uppercase tracking-wider text-clara-cream/60 hover:text-clara-cream transition-colors">
                  <ArrowLeft className="w-4 h-4" /> {F.back}
                </button>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight mb-8">{F.q3}</h3>
                <div className="flex flex-wrap gap-3">
                  {PREFS.map((pr) => (
                    <OptionBtn key={pr} testid={`gf-pref-${slug(pr)}`} selected={pref === pr} onClick={() => { setPref(pr); setStep(4); }}>
                      {pr === "None" ? F.noPref : P.types[pr]}
                    </OptionBtn>
                  ))}
                </div>
                <button onClick={() => setStep(2)} data-testid="gf-back-3" className="mt-8 inline-flex items-center gap-2 font-data text-xs uppercase tracking-wider text-clara-cream/60 hover:text-clara-cream transition-colors">
                  <ArrowLeft className="w-4 h-4" /> {F.back}
                </button>
              </motion.div>
            )}

            {step === 4 && result && (
              <motion.div key="s4" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight mb-2" data-testid="gf-result-title">{F.resultTitle}</h3>
                <p className="text-sm text-clara-darkmuted mb-8" data-testid="gf-result-reason">{result.relaxed ? F.resultFallback : F.resultReason}</p>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {result.results.map((p) => (
                    <div key={p.code} data-testid={`gf-result-${p.code}`} className="border border-clara-cream/15 overflow-hidden">
                      <button onClick={() => go(lp(`/grades/${p.code}`))} className="relative block w-full aspect-[16/9] overflow-hidden" style={{ backgroundColor: p.hex }}>
                        <img src={p.image} alt={`Clara ${p.code} cocoa powder`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                      </button>
                      <div className="p-4">
                        <div className="font-display text-2xl mb-1">{p.code}</div>
                        <div className="text-xs text-clara-darkmuted mb-4">{P.colours[p.color]} · pH {p.ph} · {P.types[p.type]}</div>
                        <div className="flex gap-2">
                          <button onClick={() => go(lp(`/grades/${p.code}`))} data-testid={`gf-view-${p.code}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-clara-cream/30 px-4 py-2 text-xs font-semibold hover:border-clara-cream transition-colors">
                            {F.viewGrade}
                          </button>
                          <button onClick={() => go(`${lp("/contact")}?grade=${p.code}`)} data-testid={`gf-sample-${p.code}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-clara-terracotta px-4 py-2 text-xs font-semibold text-clara-cream hover:bg-clara-rust transition-colors">
                            {F.requestSample} <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => { setStep(1); setApp(null); setColour(null); setPref(null); }} data-testid="gf-restart-btn" className="inline-flex items-center gap-2 font-data text-xs uppercase tracking-wider text-clara-cream/60 hover:text-clara-cream transition-colors">
                  <RotateCcw className="w-4 h-4" /> {F.restart}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
